import {
  useLogoutMutation,
  useWhatsappAccountsQuery,
  type WhatsappAccount,
} from '@queries/whatsapp/accounts';
import { useWhatsappChatsQuery, type WhatsappChat } from '@queries/whatsapp/chats';
import {
  useSendTextMutation,
  useWhatsappMessagesQuery,
  type WhatsappMessage,
} from '@queries/whatsapp/messages';
import { createEffect, createMemo, createSignal, For, Show } from 'solid-js';
import { AddAccountModal } from './AddAccountModal';
import { BroadcastModal } from './BroadcastModal';
import { NewChatModal } from './NewChatModal';

/** Media message types worth showing even when they carry no text body. */
const MEDIA_TYPES = new Set([
  'IMAGE',
  'VIDEO',
  'AUDIO',
  'VOICE',
  'PTT',
  'GIF',
  'DOCUMENT',
  'STICKER',
  'LOCATION',
  'LIVE_LOCATION',
  'CONTACT',
  'CONTACT_CARD',
  'CONTACTS_ARRAY',
  'POLL',
]);

/** WhatsApp companions receive a stream of protocol/housekeeping messages
 * (key distribution, app-state syncs, disappearing-message timers, receipts)
 * that aren't conversation content. Keep only real text/media. */
function isContentMessage(m: WhatsappMessage): boolean {
  if (m.body && m.body.trim().length > 0) return true;
  return MEDIA_TYPES.has((m.msgType || '').toUpperCase());
}

/** A short label for a media message that has no text body. */
function mediaLabel(msgType: string): string {
  const t = (msgType || '').toUpperCase();
  if (t === 'IMAGE') return '📷 Photo';
  if (t === 'VIDEO' || t === 'GIF') return '🎬 Video';
  if (t === 'AUDIO' || t === 'VOICE' || t === 'PTT') return '🎙️ Voice message';
  if (t === 'DOCUMENT') return '📎 Document';
  if (t === 'STICKER') return '🌟 Sticker';
  if (t === 'LOCATION' || t === 'LIVE_LOCATION') return '📍 Location';
  if (t === 'CONTACT' || t === 'CONTACT_CARD' || t === 'CONTACTS_ARRAY')
    return '👤 Contact';
  return `[${msgType}]`;
}

/** Turn a WhatsApp jid into a friendly label. */
function jidLabel(jid: string): string {
  const user = jid.split('@')[0] ?? jid;
  if (jid.endsWith('@g.us')) return `Group ${user}`;
  return `+${user}`;
}

/** Clean a chat-list preview: hide bare protocol/system tokens the gateway may
 * surface as the last message (e.g. "ProtocolMessage"). */
function chatPreview(chat: WhatsappChat): string {
  const body = chat.lastBody?.trim();
  if (!body) return 'No messages yet';
  const compact = body.replace(/[\s_]/g, '').toLowerCase();
  if (
    /^(protocol|senderkey|keydistribution|appstate|historysync|reaction)message?$/.test(
      compact
    )
  ) {
    return '…';
  }
  return body;
}

/** A friendly name for an account row. */
function accountLabel(a: WhatsappAccount): string {
  return (
    a.displayName ||
    (a.phoneE164 ? `+${a.phoneE164.replace(/^\+/, '')}` : a.accountId.slice(0, 8))
  );
}

/**
 * Multi-account WhatsApp chat inbox. Left pane: account switcher + conversation
 * list; right pane: the selected conversation's thread + compose box. Data is
 * proxied from the WhatsApp gateway via the DSS GraphQL soup endpoint and polled
 * for freshness.
 */
export function WhatsAppInbox() {
  const accounts = useWhatsappAccountsQuery();
  const logout = useLogoutMutation();
  const [selectedAccount, setSelectedAccount] = createSignal<
    string | undefined
  >();
  const [selectedChat, setSelectedChat] = createSignal<string | undefined>();
  const [showAdd, setShowAdd] = createSignal(false);
  const [showBroadcast, setShowBroadcast] = createSignal(false);
  const [showNewChat, setShowNewChat] = createSignal(false);
  const [draft, setDraft] = createSignal('');

  // Default to the first connected (else first) account once accounts load.
  createEffect(() => {
    if (selectedAccount()) return;
    const list = accounts.data;
    if (!list?.length) return;
    const preferred = list.find((a) => a.state === 'CONNECTED') ?? list[0];
    setSelectedAccount(preferred.accountId);
  });

  const currentAccount = createMemo(() =>
    accounts.data?.find((a) => a.accountId === selectedAccount())
  );

  const chats = useWhatsappChatsQuery(selectedAccount);
  const messages = useWhatsappMessagesQuery(selectedAccount, selectedChat);
  const sendText = useSendTextMutation();

  // Only real conversation content — protocol/system noise is dropped.
  const visibleMessages = createMemo(() =>
    (messages.data ?? []).filter(isContentMessage)
  );

  // Display name for the open conversation: contact push name if known, else
  // the phone/jid. Falls back cleanly for a brand-new chat not yet in the list.
  const selectedChatLabel = createMemo(() => {
    const jid = selectedChat();
    if (!jid) return '';
    const c = chats.data?.find((x) => x.chatJid === jid);
    return c?.name?.trim() || jidLabel(jid);
  });
  const isGroupChat = () => selectedChat()?.endsWith('@g.us') ?? false;

  async function send() {
    const account = selectedAccount();
    const chat = selectedChat();
    const text = draft().trim();
    if (!account || !chat || !text) return;
    setDraft('');
    await sendText.mutateAsync({
      accountId: account,
      to: chat,
      text,
      isGroup: chat.endsWith('@g.us'),
    });
  }

  return (
    <div class="flex h-full w-full">
      {/* Left: accounts + conversations */}
      <div class="flex w-80 shrink-0 flex-col border-r border-neutral-200 dark:border-neutral-800">
        {/* Account switcher */}
        <div class="flex flex-col gap-2 border-b border-neutral-200 p-3 dark:border-neutral-800">
          <div class="flex items-center gap-2">
            <select
              class="min-w-0 flex-1 rounded border border-neutral-300 bg-transparent px-2 py-1 text-sm dark:border-neutral-700"
              value={selectedAccount() ?? ''}
              onChange={(e) => {
                setSelectedAccount(e.currentTarget.value || undefined);
                setSelectedChat(undefined);
              }}
            >
              <Show when={(accounts.data?.length ?? 0) === 0}>
                <option value="">No accounts linked</option>
              </Show>
              <For each={accounts.data ?? []}>
                {(a) => <option value={a.accountId}>{accountLabel(a)}</option>}
              </For>
            </select>
            <button
              class="shrink-0 rounded bg-green-600 px-2 py-1 text-sm text-white"
              title="Link a new WhatsApp account"
              onClick={() => setShowAdd(true)}
            >
              + Add
            </button>
          </div>

          {/* Current-account status + management */}
          <Show when={currentAccount()}>
            {(acct) => (
              <div class="flex items-center justify-between gap-2 text-xs">
                <span class="flex min-w-0 items-center gap-1.5 text-neutral-500">
                  <span
                    class="inline-block h-2 w-2 shrink-0 rounded-full"
                    classList={{
                      'bg-green-500': acct().state === 'CONNECTED',
                      'bg-amber-500':
                        acct().state === 'PENDING' || acct().state === 'LINKED',
                      'bg-neutral-400':
                        acct().state !== 'CONNECTED' &&
                        acct().state !== 'PENDING' &&
                        acct().state !== 'LINKED',
                    }}
                  />
                  <span class="truncate">
                    {acct().phoneE164 ? `+${acct().phoneE164.replace(/^\+/, '')}` : '—'}
                    {' · '}
                    {acct().state.toLowerCase()}
                  </span>
                </span>
                <button
                  class="shrink-0 text-neutral-400 hover:text-red-500 disabled:opacity-50"
                  disabled={logout.isPending}
                  title="Log this account out"
                  onClick={() => {
                    if (
                      confirm(
                        `Log out ${accountLabel(acct())}? You'll need to re-scan to use it again.`
                      )
                    ) {
                      void logout.mutateAsync({ accountId: acct().accountId });
                      setSelectedAccount(undefined);
                      setSelectedChat(undefined);
                    }
                  }}
                >
                  Log out
                </button>
              </div>
            )}
          </Show>

          {/* Actions */}
          <div class="flex gap-2">
            <button
              class="flex-1 rounded border border-green-600 px-2 py-1 text-sm text-green-700 disabled:opacity-50 dark:text-green-400"
              disabled={!selectedAccount()}
              onClick={() => setShowNewChat(true)}
            >
              ✚ New chat
            </button>
            <button
              class="flex-1 rounded border border-green-600 px-2 py-1 text-sm text-green-700 disabled:opacity-50 dark:text-green-400"
              title="Broadcast to many recipients (群发)"
              disabled={!selectedAccount()}
              onClick={() => setShowBroadcast(true)}
            >
              群发
            </button>
          </div>
        </div>

        {/* Conversation list */}
        <div class="min-h-0 flex-1 overflow-y-auto">
          <Show
            when={(chats.data?.length ?? 0) > 0}
            fallback={
              <p class="p-4 text-sm text-neutral-500">
                {selectedAccount()
                  ? 'No conversations yet. Start one with “New chat”, or wait for an incoming message.'
                  : 'Link a WhatsApp account to begin.'}
              </p>
            }
          >
            <For each={chats.data ?? []}>
              {(chat) => (
                <button
                  class="flex w-full flex-col gap-0.5 border-b border-neutral-100 px-4 py-2 text-left hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-800"
                  classList={{
                    'bg-neutral-100 dark:bg-neutral-800':
                      selectedChat() === chat.chatJid,
                  }}
                  onClick={() => setSelectedChat(chat.chatJid)}
                >
                  <span class="truncate text-sm font-medium">
                    {chat.name?.trim() || jidLabel(chat.chatJid)}
                  </span>
                  <span class="truncate text-xs text-neutral-500">
                    {chat.lastFromMe ? 'You: ' : ''}
                    {chatPreview(chat)}
                  </span>
                </button>
              )}
            </For>
          </Show>
        </div>
      </div>

      {/* Right: thread + compose */}
      <div class="flex min-w-0 flex-1 flex-col">
        <Show
          when={selectedChat()}
          fallback={
            <div class="flex flex-1 items-center justify-center text-sm text-neutral-500">
              Select a conversation
            </div>
          }
        >
          <div class="border-b border-neutral-200 p-3 text-sm font-medium dark:border-neutral-800">
            {selectedChatLabel()}
          </div>
          <div class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4">
            <Show
              when={visibleMessages().length > 0}
              fallback={
                <p class="m-auto text-sm text-neutral-400">
                  No messages in this conversation yet.
                </p>
              }
            >
              <For each={visibleMessages()}>
                {(m) => (
                  <div
                    class="max-w-[70%] rounded-lg px-3 py-2 text-sm"
                    classList={{
                      'self-end bg-green-600 text-white': m.fromMe,
                      'self-start bg-neutral-100 dark:bg-neutral-800': !m.fromMe,
                    }}
                  >
                    <Show when={isGroupChat() && !m.fromMe && m.senderName?.trim()}>
                      <div class="mb-0.5 text-xs font-medium opacity-70">
                        {m.senderName}
                      </div>
                    </Show>
                    <Show
                      when={m.body && m.body.trim()}
                      fallback={
                        <span class="italic opacity-70">
                          {mediaLabel(m.msgType)}
                        </span>
                      }
                    >
                      {m.body}
                    </Show>
                  </div>
                )}
              </For>
            </Show>
          </div>
          <div class="flex items-center gap-2 border-t border-neutral-200 p-3 dark:border-neutral-800">
            <textarea
              rows={1}
              class="min-w-0 flex-1 resize-none rounded border border-neutral-300 bg-transparent px-3 py-2 text-sm dark:border-neutral-700"
              placeholder="Type a message"
              value={draft()}
              onInput={(e) => setDraft(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <button
              class="shrink-0 rounded bg-green-600 px-4 py-2 text-sm text-white disabled:opacity-50"
              disabled={sendText.isPending || !draft().trim()}
              onClick={() => void send()}
            >
              Send
            </button>
          </div>
        </Show>
      </div>

      <Show when={showAdd()}>
        <AddAccountModal onClose={() => setShowAdd(false)} />
      </Show>
      <Show when={showBroadcast()}>
        <BroadcastModal
          accountId={selectedAccount()}
          onClose={() => setShowBroadcast(false)}
        />
      </Show>
      <Show when={showNewChat()}>
        <NewChatModal
          accountId={selectedAccount()}
          onClose={() => setShowNewChat(false)}
          onStarted={(chatJid) => setSelectedChat(chatJid)}
        />
      </Show>
    </div>
  );
}
